// IronsIssues — IRONS issue tracking dashboard
// Sections: KPI row, open issue breakdown, resolution performance,
// aging issues alert list, volume trends, repeat problem units.

import { useMemo, useState } from "react";
import {
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import { useIrons } from "../hooks/useOps";
import type { IronRow } from "../hooks/useOps";
import { useAggregates } from "../hooks/useDashboard";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import AlertList from "../components/dashboard/AlertList";
import type { AlertItem } from "../components/dashboard/AlertList";
import StatusBadge from "../components/dashboard/StatusBadge";
import TruncationNotice from "../components/shared/TruncationNotice";
import { useTheme } from "../hooks/useTheme";
import { getChartTheme } from "../lib/chart-theme";
import DateRangeToggle, {
  useSinceDate,
  isAfterSince,
} from "../components/dashboard/DateRangeToggle";
import type { DateRange } from "../components/dashboard/DateRangeToggle";

// ── Constants ─────────────────────────────────────────────────────────────────

const DCVFD_ACCENT = "#2a7a6c";
const COLOR_CREATED = "#3b82f6"; // blue — issues created
const COLOR_RESOLVED = "#22c55e"; // green — issues resolved
const COLOR_GOOD = "#22c55e"; // avg resolution ≤14 days
const COLOR_BAD = "#dc2626"; // avg resolution >14 days
const COLOR_LINE = "#f59e0b"; // amber — % resolved within 14 days line

const CATEGORIES = [
  "Apparatus",
  "Equipment",
  "Facilities",
  "IT",
  "SCBA",
] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function asString(v: unknown, fallback = ""): string {
  if (typeof v === "string") return v;
  return fallback;
}

/** Days between two date strings (a - b). Positive means a is later. */
function daysBetween(
  a: string | null | undefined,
  b: string | null | undefined,
): number | null {
  if (!a || !b) return null;
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  if (Number.isNaN(da) || Number.isNaN(db)) return null;
  return Math.round((da - db) / 86_400_000);
}

/** Age in days from created_on to today. */
function ageFromNow(createdOn: string | null | undefined): number | null {
  if (!createdOn) return null;
  const d = new Date(createdOn).getTime();
  if (Number.isNaN(d)) return null;
  return Math.round((Date.now() - d) / 86_400_000);
}

/** Format a date string as "Mon 'YY" */
function toMonthLabel(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const mon = d.toLocaleString("en-US", { month: "short" });
  const yr = String(d.getFullYear()).slice(2);
  return `${mon} '${yr}`;
}

/** Build a sorted array of the 12 most recent months as YYYY-MM strings. */
function last12Months(): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
    );
  }
  return months;
}

function issueYearMonth(row: IronRow): string {
  const s = asString(row.created_on as unknown);
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function resolutionYearMonth(row: IronRow): string {
  const s = asString(row.resolution_date as unknown);
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// ── Derived data builders ─────────────────────────────────────────────────────

interface CategoryCount {
  name: string;
  count: number;
}
interface StationCount {
  name: string;
  count: number;
}

function buildCategoryCounts(openIssues: IronRow[]): CategoryCount[] {
  const map = new Map<string, number>();
  for (const row of openIssues) {
    const cat = asString(row.category as unknown, "Other");
    map.set(cat, (map.get(cat) ?? 0) + 1);
  }
  // Canonical categories first, then any extras
  const result: CategoryCount[] = [];
  for (const cat of CATEGORIES) {
    if (map.has(cat)) result.push({ name: cat, count: map.get(cat)! });
  }
  for (const [k, v] of map.entries()) {
    if (!CATEGORIES.includes(k as (typeof CATEGORIES)[number])) {
      result.push({ name: k, count: v });
    }
  }
  return result.sort((a, b) => b.count - a.count);
}

function buildStationCounts(openIssues: IronRow[]): StationCount[] {
  const map = new Map<string, number>();
  for (const row of openIssues) {
    const station = asString(row.station as unknown, "Unknown");
    map.set(station, (map.get(station) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

interface MonthResolution {
  month: string; // display label e.g. "Apr '25"
  ym: string; // YYYY-MM
  avgDays: number | null; // null when no closures that month
  pctWithin14: number | null; // null when no closures that month
}

function buildResolutionPerformance(issues: IronRow[]): MonthResolution[] {
  const months = last12Months();
  // Closed issues only — those with resolution_date
  const closed = issues.filter(
    (r) =>
      resolutionYearMonth(r) !== "" && asString(r.resolution_date as unknown),
  );

  return months.map((ym) => {
    const monthClosed = closed.filter((r) => resolutionYearMonth(r) === ym);
    if (monthClosed.length === 0) {
      return {
        month: toMonthLabel(`${ym}-01`),
        ym,
        avgDays: null,
        pctWithin14: null,
      };
    }
    const days = monthClosed
      .map((r) => {
        const created = asString(r.created_on as unknown);
        const resolved = asString(r.resolution_date as unknown);
        return daysBetween(resolved, created);
      })
      .filter((d): d is number => d !== null && d >= 0);

    const avg =
      days.length > 0 ? days.reduce((a, b) => a + b, 0) / days.length : 0;
    const within14 = days.filter((d) => d <= 14).length;
    const pct = days.length > 0 ? (within14 / days.length) * 100 : 0;

    return {
      month: toMonthLabel(`${ym}-01`),
      ym,
      avgDays: Math.round(avg * 10) / 10,
      pctWithin14: Math.round(pct),
    };
  });
}

interface MonthVolume {
  month: string;
  created: number;
  resolved: number;
}

function buildVolumeTrends(issues: IronRow[]): MonthVolume[] {
  const months = last12Months();
  return months.map((ym) => ({
    month: toMonthLabel(`${ym}-01`),
    created: issues.filter((r) => issueYearMonth(r) === ym).length,
    resolved: issues.filter((r) => resolutionYearMonth(r) === ym).length,
  }));
}

interface UnitRepeat {
  unit: string;
  station: string;
  count: number;
  mostRecent: string;
}

function buildRepeatUnits(issues: IronRow[]): UnitRepeat[] {
  const map = new Map<
    string,
    { station: string; count: number; mostRecent: string }
  >();
  for (const row of issues) {
    const unit = asString(row.unit as unknown, "").trim();
    const station = asString(row.station as unknown, "");
    const created = asString(row.created_on as unknown, "");
    if (!unit) continue;
    const existing = map.get(unit);
    if (!existing) {
      map.set(unit, { station, count: 1, mostRecent: created });
    } else {
      existing.count++;
      if (created > existing.mostRecent) existing.mostRecent = created;
    }
  }
  return Array.from(map.entries())
    .map(([unit, v]) => ({ unit, ...v }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function RefreshDot({ lastUpdatedAt }: { lastUpdatedAt: Date | null }) {
  if (!lastUpdatedAt) return null;
  const sec = Math.round((Date.now() - lastUpdatedAt.getTime()) / 1000);
  return (
    <div className="flex items-center gap-1.5 text-xs text-theme-muted">
      <span className="w-1.5 h-1.5 rounded-full bg-status-green shrink-0" />
      <span>Updated {sec < 5 ? "just now" : `${sec}s ago`}</span>
    </div>
  );
}

interface HBarProps {
  data: { name: string; count: number }[];
}

function HorizontalBar({ data }: HBarProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const chart = getChartTheme(isDark);

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2 text-center">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-status-green"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        <p className="text-sm font-medium text-status-green">No open issues</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer
      width="100%"
      height={Math.max(data.length * 36 + 30, 160)}
    >
      <BarChart
        data={data}
        layout="vertical"
        margin={{ left: 0, right: 20, top: 4, bottom: 4 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chart.gridStroke}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: chart.tickColor }}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
        />
        <YAxis
          dataKey="name"
          type="category"
          width={90}
          tick={{ fontSize: 11, fill: chart.tickColor }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          cursor={{ fill: chart.cursorFill }}
          contentStyle={{
            backgroundColor: chart.tooltipBg,
            border: `1px solid ${chart.tooltipBorder}`,
            color: chart.tooltipText,
            borderRadius: 8,
            fontSize: 12,
          }}
          formatter={(v) => [v, "Issues"]}
        />
        <Bar
          dataKey="count"
          fill={DCVFD_ACCENT}
          radius={[0, 3, 3, 0]}
          maxBarSize={22}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ResolutionPerformanceChart({ data }: { data: MonthResolution[] }) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const chart = getChartTheme(isDark);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart
        data={data}
        margin={{ left: 0, right: 30, top: 8, bottom: 4 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={chart.gridStroke} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 11, fill: chart.tickColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          yAxisId="days"
          orientation="left"
          tick={{ fontSize: 11, fill: chart.tickColor }}
          tickLine={false}
          axisLine={false}
          label={{
            value: "Avg Days",
            angle: -90,
            position: "insideLeft",
            fontSize: 11,
            fill: chart.tickColor,
            dx: -4,
          }}
        />
        <YAxis
          yAxisId="pct"
          orientation="right"
          tick={{ fontSize: 11, fill: chart.tickColor }}
          tickLine={false}
          axisLine={false}
          domain={[0, 100]}
          tickFormatter={(v) => `${v}%`}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: chart.tooltipBg,
            border: `1px solid ${chart.tooltipBorder}`,
            color: chart.tooltipText,
            borderRadius: 8,
            fontSize: 12,
          }}
          cursor={{ fill: chart.cursorFill }}
          formatter={(v, name) => {
            if (name === "pctWithin14") return [`${v}%`, "% Within 14 Days"];
            return [`${v} days`, "Avg Resolution Days"];
          }}
        />
        <Legend
          formatter={(value) =>
            value === "pctWithin14" ? "% Within 14 Days" : "Avg Resolution Days"
          }
          wrapperStyle={{ fontSize: 11, color: chart.labelColor }}
        />
        <Bar
          yAxisId="days"
          dataKey="avgDays"
          radius={[3, 3, 0, 0]}
          maxBarSize={32}
        >
          {data.map((entry, i) => (
            <Cell
              key={i}
              fill={(entry.avgDays ?? 0) > 14 ? COLOR_BAD : COLOR_GOOD}
            />
          ))}
        </Bar>
        <Line
          yAxisId="pct"
          type="monotone"
          dataKey="pctWithin14"
          stroke={COLOR_LINE}
          strokeWidth={2}
          dot={{ r: 3, fill: COLOR_LINE }}
          activeDot={{ r: 5 }}
          connectNulls={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function VolumeTrendsChart({ data }: { data: MonthVolume[] }) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const chart = getChartTheme(isDark);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart
        data={data}
        margin={{ left: 0, right: 8, top: 8, bottom: 4 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={chart.gridStroke} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 11, fill: chart.tickColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: chart.tickColor }}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: chart.tooltipBg,
            border: `1px solid ${chart.tooltipBorder}`,
            color: chart.tooltipText,
            borderRadius: 8,
            fontSize: 12,
          }}
          cursor={{ fill: chart.cursorFill }}
          formatter={(v, name) => [
            v,
            name === "created" ? "Created" : "Resolved",
          ]}
        />
        <Legend
          formatter={(value) =>
            value === "created" ? "Issues Created" : "Issues Resolved"
          }
          wrapperStyle={{ fontSize: 11, color: chart.labelColor }}
        />
        <Bar
          dataKey="created"
          fill={COLOR_CREATED}
          radius={[3, 3, 0, 0]}
          maxBarSize={18}
        />
        <Bar
          dataKey="resolved"
          fill={COLOR_RESOLVED}
          radius={[3, 3, 0, 0]}
          maxBarSize={18}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

interface RepeatUnitsTableProps {
  rows: UnitRepeat[];
  isLoading: boolean;
}

function RepeatUnitsTable({ rows, isLoading }: RepeatUnitsTableProps) {
  if (isLoading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-left">
          <thead>
            <tr className="border-b border-theme-subtle">
              {["#", "Unit", "Station", "Issue Count", "Most Recent"].map(
                (h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-b border-theme-subtle">
                {Array.from({ length: 5 }).map((__, j) => (
                  <td key={j} className="px-4 py-3">
                    <div
                      className="skeleton h-4 rounded-md"
                      style={{ width: `${40 + ((i * 11 + j * 13) % 50)}px` }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 flex flex-col items-center gap-2 text-center">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-status-green"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        <p className="text-sm font-medium text-status-green">
          No repeat problem units
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider w-8">
              #
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Unit
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Station
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Issue Count
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Most Recent
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row.unit}
              className="hover:bg-theme-overlay/50 transition-colors"
            >
              <td className="px-4 py-3 text-sm text-theme-muted">{idx + 1}</td>
              <td className="px-4 py-3 text-sm font-semibold text-theme-primary">
                {idx === 0 && (
                  <span className="inline-block w-2 h-2 rounded-full bg-status-red mr-2 shrink-0" />
                )}
                {row.unit}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary">
                {row.station || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-right">
                <span
                  className={`font-bold ${idx === 0 ? "text-status-red" : idx === 1 ? "text-status-amber" : "text-theme-primary"}`}
                >
                  {row.count}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-right text-theme-secondary">
                {row.mostRecent
                  ? new Date(row.mostRecent).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "2-digit",
                    })
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function IronsIssues() {
  const INTERVAL = 120_000;

  const [range, setRange] = useState<DateRange>("all");
  const sinceDate = useSinceDate(range);

  const {
    data: ironsData,
    isLoading: ironsLoading,
    lastUpdatedAt,
  } = useIrons(undefined, INTERVAL);
  const { data: aggregatesData, isLoading: aggLoading } = useAggregates(60_000);

  const allIssues: IronRow[] = ironsData?.data ?? [];

  // ── Date range filter: filter issues by created_on ────────────────────────
  const issues = useMemo<IronRow[]>(() => {
    if (!sinceDate) return allIssues;
    return allIssues.filter((r) =>
      isAfterSince(asString(r.created_on as unknown), sinceDate),
    );
  }, [allIssues, sinceDate]);
  const agg = aggregatesData?.aggregates ?? {};

  // ── KPI values ────────────────────────────────────────────────────────────
  const openIssuesCount = asNumber(agg["open_irons_count"]);
  const avgResolutionDays = asNumber(agg["avg_irons_resolution_days"]);

  // Closed this month and issues this month — derive from live data
  const { closedThisMonth, issuedThisMonth } = useMemo(() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return {
      closedThisMonth: issues.filter((r) => resolutionYearMonth(r) === ym)
        .length,
      issuedThisMonth: issues.filter((r) => issueYearMonth(r) === ym).length,
    };
  }, [issues]);

  // Open issues: status != 'Closed'
  const openIssues = useMemo(
    () =>
      issues.filter(
        (r) => asString(r.status as unknown).toLowerCase() !== "closed",
      ),
    [issues],
  );

  // ── Category + station breakdown ──────────────────────────────────────────
  const categoryCounts = useMemo(
    () => buildCategoryCounts(openIssues),
    [openIssues],
  );
  const stationCounts = useMemo(
    () => buildStationCounts(openIssues),
    [openIssues],
  );

  // ── Resolution performance (12 months) ───────────────────────────────────
  const resolutionData = useMemo(
    () => buildResolutionPerformance(issues),
    [issues],
  );

  // ── Aging issues (>30 days, unresolved) ───────────────────────────────────
  const agingAlerts = useMemo<AlertItem[]>(() => {
    return openIssues
      .flatMap((r) => {
        const age = ageFromNow(asString(r.created_on as unknown) || null);
        if (age === null || age <= 30) return [];
        const subject = asString(r.subject as unknown, "Untitled Issue");
        const station = asString(r.station as unknown, "");
        const unit = asString(r.unit as unknown, "");
        const severity: AlertItem["severity"] =
          age > 90 ? "critical" : age > 60 ? "warning" : "info";
        const parts = [station, unit].filter(Boolean).join(" · ");
        const item: AlertItem = {
          id: asString(r.id as unknown) || `${subject}-${age}`,
          title: subject,
          subtitle: parts || undefined,
          severity,
          meta: `${age}d old`,
        };
        return [item];
      })
      .sort((a, b) => {
        const sevOrder = { critical: 0, warning: 1, info: 2 };
        return sevOrder[a.severity] - sevOrder[b.severity];
      });
  }, [openIssues]);

  // ── Volume trends ─────────────────────────────────────────────────────────
  const volumeData = useMemo(() => buildVolumeTrends(issues), [issues]);

  // ── Repeat units ──────────────────────────────────────────────────────────
  const repeatUnits = useMemo(() => buildRepeatUnits(issues), [issues]);

  // ── KPI accents ───────────────────────────────────────────────────────────
  const openAccent: "red" | "amber" | "neutral" =
    openIssuesCount > 50 ? "red" : openIssuesCount > 20 ? "amber" : "neutral";

  const resolutionAccent: "red" | "amber" | "green" | "neutral" =
    avgResolutionDays === 0
      ? "neutral"
      : avgResolutionDays > 21
        ? "red"
        : avgResolutionDays > 14
          ? "amber"
          : "green";

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            IRONS Issues
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Issue tracking for apparatus, equipment, facilities, IT, and SCBA
            across all stations.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="Live" variant="green" />
          <RefreshDot lastUpdatedAt={lastUpdatedAt} />
        </div>
      </div>

      {/* ── Row-completeness notice ───────────────────────────────────
          dash_irons_issues held 24,896 rows on 2026-08-27 against the API's
          5,000-row hard cap, so an unfiltered read of this page is missing
          roughly 80% of the table. The cap is deliberately not raised — this
          notice is the honest outcome until the endpoint aggregates
          server-side. */}
      <TruncationNotice
        response={ironsData}
        label="IRONS issue"
        // NOT a filter suggestion. This page has no filter control
        // (useIrons(undefined, ...)), and dash_irons_issues.Station holds
        // Airtable record IDs rather than station names, so even ?station=FS13
        // returns zero rows. IRONS is ~80% truncated, so this banner is on
        // screen every time the page loads - an instruction that provably does
        // nothing would train readers to ignore the one control this change
        // exists to add. Say the true thing instead.
        hint="This table is larger than a single page can load, so every count here is a floor, not a total. Loading it whole needs server-side aggregation, which is not built yet."
      />

      {/* ── Date Range Toggle ─────────────────────────────────────────── */}
      <DateRangeToggle value={range} onChange={setRange} />

      {/* ── KPI Row ──────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KPICard
            label="Open Issues"
            value={aggLoading ? "—" : openIssuesCount}
            subtitle="unresolved issues · all-time"
            accent={aggLoading ? "neutral" : openAccent}
            isLoading={aggLoading}
            trend={
              !aggLoading
                ? {
                    direction: openIssuesCount === 0 ? "down" : "up",
                    label:
                      openIssuesCount === 0
                        ? "All resolved"
                        : openIssuesCount > 50
                          ? "Critical backlog"
                          : "Active",
                    positiveIsGood: false,
                  }
                : undefined
            }
          />

          <KPICard
            label="Avg Resolution Days"
            value={
              aggLoading
                ? "—"
                : avgResolutionDays > 0
                  ? avgResolutionDays.toFixed(1)
                  : "No data"
            }
            subtitle="target: ≤14 days · all-time"
            accent={aggLoading ? "neutral" : resolutionAccent}
            isLoading={aggLoading}
            trend={
              !aggLoading && avgResolutionDays > 0
                ? {
                    direction: avgResolutionDays <= 14 ? "down" : "up",
                    label:
                      avgResolutionDays <= 14 ? "On target" : "Above target",
                    positiveIsGood: false,
                  }
                : undefined
            }
          />

          <KPICard
            label="Closed This Month"
            value={ironsLoading ? "—" : closedThisMonth}
            subtitle="resolved in current month"
            accent={closedThisMonth > 0 ? "green" : "neutral"}
            isLoading={ironsLoading}
            trend={
              !ironsLoading && closedThisMonth > 0
                ? {
                    direction: "up",
                    label: `${closedThisMonth} resolved`,
                    positiveIsGood: true,
                  }
                : undefined
            }
          />

          <KPICard
            label="Issues This Month"
            value={ironsLoading ? "—" : issuedThisMonth}
            subtitle={
              !ironsLoading && closedThisMonth > 0
                ? `${closedThisMonth} closed — net ${issuedThisMonth - closedThisMonth > 0 ? "+" : ""}${issuedThisMonth - closedThisMonth}`
                : "created in current month"
            }
            accent={
              !ironsLoading && issuedThisMonth > closedThisMonth
                ? "amber"
                : "neutral"
            }
            isLoading={ironsLoading}
          />
        </div>
      </section>

      {/* ── Section 1: Open Issue Breakdown ──────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Open Issue Breakdown
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard
            title="By Category"
            subtitle="Open issues grouped by category"
            headerRight={
              ironsLoading ? (
                <span className="text-xs text-theme-muted animate-pulse">
                  Loading...
                </span>
              ) : (
                <StatusBadge
                  label={`${openIssues.length} open`}
                  variant="muted"
                />
              )
            }
            minHeight={160}
          >
            <HorizontalBar data={categoryCounts} />
          </ChartCard>

          <ChartCard
            title="By Station"
            subtitle="Open issues grouped by station"
            headerRight={
              ironsLoading ? (
                <span className="text-xs text-theme-muted animate-pulse">
                  Loading...
                </span>
              ) : (
                <StatusBadge
                  label={`${stationCounts.length} stations`}
                  variant="muted"
                />
              )
            }
            minHeight={160}
          >
            <HorizontalBar data={stationCounts} />
          </ChartCard>
        </div>
      </section>

      {/* ── Section 2: Resolution Performance ────────────────────────── */}
      <section>
        <ChartCard
          title="Resolution Performance"
          subtitle="Monthly avg resolution days (bars) and % resolved within 14 days (amber line) — red bars indicate avg >14 days"
          headerRight={
            ironsLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : (
              <StatusBadge label="12 months" variant="blue" />
            )
          }
          minHeight={280}
        >
          <ResolutionPerformanceChart data={resolutionData} />
        </ChartCard>
      </section>

      {/* ── Section 3: Aging Issues ───────────────────────────────────── */}
      <section>
        <AlertList
          title="Aging Issues (>30 Days Unresolved)"
          items={agingAlerts}
          emptyMessage="No issues older than 30 days — excellent response time!"
          isLoading={ironsLoading}
          maxVisible={10}
        />
      </section>

      {/* ── Section 4: Volume Trends ──────────────────────────────────── */}
      <section>
        <ChartCard
          title="Volume Trends"
          subtitle="Monthly issues created vs resolved — persistent gap between creation and resolution indicates backlog growth"
          headerRight={
            ironsLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : (
              <StatusBadge label="12 months" variant="blue" />
            )
          }
          minHeight={280}
        >
          <VolumeTrendsChart data={volumeData} />
        </ChartCard>
      </section>

      {/* ── Section 5: Top Problem Units ──────────────────────────────── */}
      <section>
        <ChartCard
          title="Top Problem Units"
          subtitle="Apparatus / units ranked by issue count — top 10 shown"
          headerRight={
            ironsLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : repeatUnits.length > 0 ? (
              <StatusBadge
                label={`${repeatUnits.length} units`}
                variant="muted"
              />
            ) : (
              <StatusBadge label="No data" variant="muted" />
            )
          }
          minHeight={0}
        >
          <RepeatUnitsTable rows={repeatUnits} isLoading={ironsLoading} />
        </ChartCard>
      </section>
    </div>
  );
}
